import { useEffect, useMemo, useRef, useState } from 'react'
import {
  alpha,
  Alert,
  Box,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { Close } from '@mui/icons-material'
import { CarnivoreModalProps } from '../../_types/modal-props.interface'
import { LocalStorageHelper } from '../../_helpers/localstorage.helper'
import {
  computePositionSize,
  DEFAULT_ALLOC_PCT,
  DEFAULT_PORTFOLIO,
  DEFAULT_RISK_PCT,
  parseMoney
} from '../../_helpers/position-size.helper'
import { StockService } from '../../services/stock.service'

// Long enough that a four-letter symbol is one request, short enough that the
// price feels like it arrives on its own.
const QUOTE_DEBOUNCE_MS = 450

const TABULAR = { fontVariantNumeric: 'tabular-nums' } as const

function money (n: number | null): string {
  if (n == null) return '—'
  return `$${n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
}

function formatPortfolio (raw: string): string {
  const digits = raw.replace(/[^\d]/g, '')
  if (!digits) return ''
  return Number(digits).toLocaleString('en-US')
}

/** Label left, figure right, decimals aligned — an order ticket, not a card. */
function LedgerRow ({
  label,
  value,
  color,
  emphasis
}: {
  label: string
  value: string
  color?: string
  emphasis?: boolean
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 2,
        py: 0.75
      }}
    >
      <Typography variant='body2' color='text.secondary'>
        {label}
      </Typography>
      <Typography
        sx={{
          ...TABULAR,
          fontSize: emphasis ? 20 : 16,
          fontWeight: emphasis ? 500 : 400,
          letterSpacing: '-0.01em',
          color: color ?? 'text.primary'
        }}
      >
        {value}
      </Typography>
    </Box>
  )
}

export default function CalculatorModal (props: CarnivoreModalProps) {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const accent = theme.palette.primary.main
  // Deliberately not wired to the global error snackbar. The symbol is fetched
  // on a debounce as the user types, so a half-typed or unknown ticker is an
  // ordinary state here, not an app-level failure — it belongs in the field, not
  // in a toast over the whole app. Failures surface as `quoteError` below.
  const stockService = useMemo(() => new StockService(() => {}), [])

  const [symbol, setSymbol] = useState('')
  // formatPortfolio also normalises: the field renders its own "$" adornment
  // now, so a value stored with one baked in has to have it stripped.
  const [portfolio, setPortfolio] = useState(() =>
    formatPortfolio(
      LocalStorageHelper.getCalculatorPortfolio() ?? String(DEFAULT_PORTFOLIO)
    )
  )
  const [allocPct, setAllocPct] = useState(String(DEFAULT_ALLOC_PCT))
  const [riskPct, setRiskPct] = useState(String(DEFAULT_RISK_PCT))
  const [last, setLast] = useState('')
  // True once the buy price is edited by hand, so the note and the badge can
  // say the size came off a typed price rather than the feed.
  const [lastTyped, setLastTyped] = useState(false)
  const [loading, setLoading] = useState(false)
  const [quoteError, setQuoteError] = useState<string | null>(null)
  const [flash, setFlash] = useState(false)
  // Guards against a slow request for an old symbol landing after a newer one.
  const requestId = useRef(0)

  useEffect(() => {
    LocalStorageHelper.setCalculatorPortfolio(portfolio)
  }, [portfolio])

  // The price arrives on its own a beat after typing stops — no Enter, no
  // button. Every other field is local arithmetic and never reaches the network.
  useEffect(() => {
    const ticker = symbol.trim().toUpperCase().replace(/^\$/, '')
    if (!ticker) {
      requestId.current++
      setLoading(false)
      setQuoteError(null)
      return
    }
    const id = ++requestId.current
    setLoading(true)
    setQuoteError(null)
    const timer = setTimeout(async () => {
      const res = await stockService.getStockInfo(ticker)
      if (id !== requestId.current) return
      setLoading(false)
      const price = Number(res?.stockInfo?.currentPrice)
      if (!res?.stockInfo || !Number.isFinite(price) || price <= 0) {
        setQuoteError(`No price for ${ticker}. Check the symbol, or type a buy price.`)
        return
      }
      setLast(money(price))
      setLastTyped(false)
      setFlash(true)
    }, QUOTE_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [symbol, stockService])

  useEffect(() => {
    if (!flash) return
    const timer = setTimeout(() => setFlash(false), 900)
    return () => clearTimeout(timer)
  }, [flash])

  const result = computePositionSize({
    portfolio: parseMoney(portfolio),
    allocPct: parseMoney(allocPct),
    riskPct: parseMoney(riskPct),
    last: parseMoney(last),
    lastTyped
  })
  const sized = 'error' in result ? null : result
  const hasPrice = sized?.last != null

  const onSymbolChange = (value: string) => {
    const next = value.toUpperCase().replace(/[^A-Z.]/g, '')
    // A feed price belongs to the symbol it came from. Drop it the moment the
    // symbol changes so a stale quote can never sit under a new ticker. A
    // hand-typed price is the user's, so it stays.
    if (next !== symbol && !lastTyped) setLast('')
    setSymbol(next)
  }

  const dial = {
    flex: 1,
    minWidth: 116,
    '& .MuiInputBase-input': { ...TABULAR, fontWeight: 500 }
  }

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      fullWidth
      maxWidth='sm'
      fullScreen={fullScreen}
      aria-labelledby='calculator-title'
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3, backgroundImage: 'none' } }}
    >
      <Box sx={{ p: { xs: 2.5, sm: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3.5
          }}
        >
          <Typography
            id='calculator-title'
            sx={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.02em' }}
          >
            Position calculator
          </Typography>
          <IconButton onClick={props.onClose} aria-label='Close calculator' size='small'>
            <Close fontSize='small' />
          </IconButton>
        </Box>

        {/* What you're buying, and at what price. */}
        <Stack direction='row' spacing={2} sx={{ mb: 2.5 }}>
          <TextField
            label='Symbol'
            value={symbol}
            onChange={e => onSymbolChange(e.target.value)}
            autoFocus
            inputProps={{
              maxLength: 8,
              autoComplete: 'off',
              spellCheck: false,
              'aria-describedby': 'calculator-note'
            }}
            InputProps={{
              endAdornment: loading ? (
                <InputAdornment position='end'>
                  <CircularProgress size={15} thickness={5} />
                </InputAdornment>
              ) : null,
              sx: { fontSize: 20, fontWeight: 600, letterSpacing: '0.08em' }
            }}
            helperText=' '
            sx={{ flex: 1.1 }}
          />
          <TextField
            label='Buy price'
            value={last}
            onChange={e => {
              setLast(e.target.value)
              setLastTyped(true)
            }}
            onBlur={() => {
              const n = parseMoney(last)
              if (n != null && Number.isFinite(n) && n > 0) setLast(money(n))
            }}
            inputProps={{ inputMode: 'decimal' }}
            helperText={
              loading
                ? 'Fetching price'
                : lastTyped && hasPrice
                ? 'Your price'
                : hasPrice
                ? 'Last traded'
                : 'Type a symbol'
            }
            sx={{
              flex: 1,
              '& .MuiInputBase-input': { ...TABULAR, fontSize: 20, fontWeight: 500 },
              '& .MuiOutlinedInput-notchedOutline': {
                transition: 'border-color 300ms ease',
                ...(flash && {
                  borderColor: accent,
                  borderWidth: 2,
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
                })
              }
            }}
          />
        </Stack>

        {/* The three dials that turn a price into a size. */}
        <Stack direction='row' spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
          <TextField
            label='Account size'
            value={portfolio}
            onChange={e => setPortfolio(formatPortfolio(e.target.value))}
            inputProps={{ inputMode: 'numeric' }}
            InputProps={{
              startAdornment: <InputAdornment position='start'>$</InputAdornment>
            }}
            sx={{ ...dial, flex: 1.4 }}
          />
          <TextField
            label='Allocation'
            value={allocPct}
            onChange={e => setAllocPct(e.target.value)}
            inputProps={{ inputMode: 'decimal' }}
            InputProps={{
              endAdornment: <InputAdornment position='end'>%</InputAdornment>
            }}
            sx={dial}
          />
          <TextField
            label='Risk to stop'
            value={riskPct}
            onChange={e => setRiskPct(e.target.value)}
            inputProps={{ inputMode: 'decimal' }}
            InputProps={{
              endAdornment: <InputAdornment position='end'>%</InputAdornment>
            }}
            sx={dial}
          />
        </Stack>

        {'error' in result && (
          <Alert severity='error' variant='outlined' sx={{ mt: 2.5 }}>
            {result.error}
          </Alert>
        )}
        {quoteError && (
          <Alert severity='warning' variant='outlined' sx={{ mt: 2.5 }}>
            {quoteError}
          </Alert>
        )}

        {/* The answer. */}
        <Box
          sx={{
            mt: 3,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            borderLeft: `3px solid ${hasPrice ? accent : alpha(accent, 0.28)}`,
            bgcolor: alpha(
              theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
              theme.palette.mode === 'dark' ? 0.035 : 0.022
            ),
            transition: 'border-color 300ms ease',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 2 }}>
            <Typography
              aria-live='polite'
              sx={{
                ...TABULAR,
                fontSize: sized?.shares != null ? { xs: 44, sm: 56 } : 24,
                fontWeight: sized?.shares != null ? 300 : 400,
                lineHeight: 1.15,
                letterSpacing: '-0.045em',
                color: sized?.shares ? accent : 'text.disabled'
              }}
            >
              {sized?.shares != null ? sized.shares.toLocaleString() : '—'}
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              {sized?.shares === 1 ? 'share' : 'shares'}
            </Typography>
          </Box>

          <LedgerRow
            label='Position value'
            value={money(sized?.positionValue ?? null)}
            emphasis
          />
          <Divider sx={{ my: 1 }} />
          <LedgerRow
            label='Stop'
            value={money(sized?.stop ?? null)}
            color={sized?.stop != null ? 'error.main' : undefined}
            emphasis
          />
          <LedgerRow
            label='Risk at stop'
            value={money(sized?.dollarRisk ?? null)}
            color={sized?.dollarRisk != null ? 'error.main' : undefined}
          />
        </Box>

        <Typography
          id='calculator-note'
          variant='body2'
          color='text.secondary'
          sx={{ mt: 2, maxWidth: '62ch' }}
        >
          {sized?.note ?? ''}
        </Typography>
      </Box>
    </Dialog>
  )
}
